import React, { useState } from 'react';
import './Settings.css';
import { useTheme } from '../../contexts/ThemeContext';
import { useSound } from '../../hooks/useSound';

const Settings = ({
                      customTime,
                      onTimeChange,
                      soundEnabled,
                      onToggleSound,
                      muted,
                      onToggleMute,
                      blindLevels,
                      onAddBlindLevel,
                      onRemoveBlindLevel,
                      onEditBlindLevel
                  }) => {
    const { darkMode } = useTheme();
    const themeClass = darkMode ? 'dark-theme' : 'light-theme';
    const { playTick, playCountdown, playWarning, playAlarm } = useSound();

    const [newSmallBlind, setNewSmallBlind] = useState('');
    const [newBigBlind, setNewBigBlind] = useState('');
    const [editIndex, setEditIndex] = useState(null);
    const [editSmall, setEditSmall] = useState('');
    const [editBig, setEditBig] = useState('');

    // Obsługa zmiany czasu rundy - zmodyfikowana funkcja
    const handleTimeInputChange = (e) => {
        const value = e.target.value;
        // Pozwalamy na puste pole oraz dowolną liczbę większą od 0
        if (value === '' || (Number(value) > 0)) {
            onTimeChange(value === '' ? '' : Number(value));
        }
    };

    // Dodawanie własnego poziomu blindów
    const handleAddBlind = () => {
        if (newSmallBlind && newBigBlind) {
            const small = Number(newSmallBlind);
            const big = Number(newBigBlind);

            if (small > 0 && big > 0) {
                onAddBlindLevel(small, big);
                setNewSmallBlind('');
                setNewBigBlind('');
            }
        }
    };

    // Rozpoczęcie edycji poziomu blindów
    const startEdit = (index, small, big) => {
        setEditIndex(index);
        setEditSmall(small.toString());
        setEditBig(big.toString());
    };

    // Zapisanie edycji poziomu blindów
    const saveEdit = () => {
        if (editIndex !== null && editSmall && editBig) {
            const small = Number(editSmall);
            const big = Number(editBig);

            if (small > 0 && big > 0) {
                onEditBlindLevel(editIndex, small, big);
                setEditIndex(null);
            }
        }
    };

    // Anulowanie edycji
    const cancelEdit = () => {
        setEditIndex(null);
    };

    return (
        <div className={`settings ${themeClass}`}>
            <h2 className="settings-title">Ustawienia</h2>

            <div className="settings-section">
                <label htmlFor="time-input">Czas rundy (minuty):</label>
                <input
                    id="time-input"
                    type="number"
                    min="1"
                    max="60"
                    value={customTime}
                    onChange={handleTimeInputChange}
                    className={`settings-input ${themeClass}`}
                />
            </div>

            <div className="settings-section">
                <div className="sound-settings">
                    <label className="sound-label">
                        <input
                            type="checkbox"
                            checked={soundEnabled}
                            onChange={onToggleSound}
                            className="sound-checkbox"
                        />
                        Dźwięki włączone
                    </label>

                    <label className="sound-label">
                        <input
                            type="checkbox"
                            checked={!muted}
                            onChange={onToggleMute}
                            className="sound-checkbox"
                            disabled={!soundEnabled}
                        />
                        Dźwięk aktywny
                    </label>
                </div>

                {soundEnabled && !muted && (
                    <div className="sound-test-buttons">
                        <h4>Test dźwięków:</h4>
                        <div className="button-row">
                            <button
                                className={`sound-test-btn ${themeClass}`}
                                onClick={playTick}
                            >
                                Tykanie
                            </button>
                            <button
                                className={`sound-test-btn ${themeClass}`}
                                onClick={playWarning}
                            >
                                Ostrzeżenie (10s)
                            </button>
                        </div>
                        <div className="button-row">
                            <button
                                className={`sound-test-btn ${themeClass}`}
                                onClick={playCountdown}
                            >
                                Odliczanie
                            </button>
                            <button
                                className={`sound-test-btn ${themeClass}`}
                                onClick={playAlarm}
                            >
                                Alarm końcowy
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="settings-section">
                <h3 className="blinds-title">Poziomy blindów</h3>

                <ul className={`blinds-list ${themeClass}`}>
                    {blindLevels.map((blind, index) => (
                        <li key={index} className={`blind-item ${themeClass}`}>
                            {editIndex === index ? (
                                <div className="blind-edit">
                                    <input
                                        type="number"
                                        value={editSmall}
                                        onChange={(e) => setEditSmall(e.target.value)}
                                        className={`blind-input ${themeClass}`}
                                        min="1"
                                    />
                                    <span className="blind-separator">/</span>
                                    <input
                                        type="number"
                                        value={editBig}
                                        onChange={(e) => setEditBig(e.target.value)}
                                        className={`blind-input ${themeClass}`}
                                        min="1"
                                    />
                                    <button
                                        onClick={saveEdit}
                                        className="blind-save-btn"
                                    >
                                        ✓
                                    </button>
                                    <button
                                        onClick={cancelEdit}
                                        className="blind-cancel-btn"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                <>
                  <span className="blind-value">
                    {blind.small} / {blind.big}
                  </span>
                                    <div className="blind-actions">
                                        <button
                                            onClick={() => startEdit(index, blind.small, blind.big)}
                                            className="blind-edit-btn"
                                        >
                                            ✎
                                        </button>
                                        <button
                                            onClick={() => onRemoveBlindLevel(index)}
                                            className="blind-remove-btn"
                                            disabled={blindLevels.length <= 1}
                                        >
                                            🗑
                                        </button>
                                    </div>
                                </>
                            )}
                        </li>
                    ))}
                </ul>

                <div className="add-blind-section">
                    <h4>Dodaj nowy poziom blindów</h4>
                    <div className="add-blind-inputs">
                        <input
                            type="number"
                            placeholder="Mała ciemna"
                            value={newSmallBlind}
                            onChange={(e) => setNewSmallBlind(e.target.value)}
                            className={`blind-input ${themeClass}`}
                            min="1"
                        />
                        <span className="blind-separator">/</span>
                        <input
                            type="number"
                            placeholder="Duża ciemna"
                            value={newBigBlind}
                            onChange={(e) => setNewBigBlind(e.target.value)}
                            className={`blind-input ${themeClass}`}
                            min="1"
                        />
                        <button
                            onClick={handleAddBlind}
                            className="add-blind-btn"
                            disabled={!newSmallBlind || !newBigBlind}
                        >
                            Dodaj
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;